import { Heading, Box, VStack, Text } from '@primerai/thread';
import { useSelector } from 'react-redux';
import { useLocation } from 'wouter';
import PasswordResetForm from './Form';
import { usePasswordRules, usePasswordReset } from './helpers';
import { withBranding, UNAUTHENTICATED } from '~/components/branding';
import { RequestReset, LinkWasSent } from '~/components/password/RequestReset';
import { RESET_TOKEN_PARAM, TEXT_SIZE, HEADING_SIZE } from '~/constants';
import { LOGIN } from '~/routes/paths';
import {
  getPasswordWasSaved,
  getResetLinkWasSent,
  getResetToken,
} from '~/store/password/selectors';

import { useTokenFromHash } from '~/utils/browser';

const Content = ({ children }: React.PropsWithChildren) => (
  <VStack>
    <Box mb="$9">
      <Heading mb="$2" size={HEADING_SIZE.LARGE}>
        Reset your password
      </Heading>
      <Text size={TEXT_SIZE.DEFAULT}>
        Enter a new password to continue signing in
      </Text>
    </Box>
    <Box>{children}</Box>
  </VStack>
);

const PasswordReset = () => {
  const [, navigate] = useLocation();
  // Token arrives either in the URL fragment (emailed reset link) or already in
  // the store (post-login forced reset, set via SET_RESET_TOKEN). Prefer the
  // fragment; fall back to the store.
  const tokenFromHash = useTokenFromHash(RESET_TOKEN_PARAM);
  const tokenFromStore = useSelector(getResetToken);
  const resetToken = tokenFromHash || tokenFromStore || '';
  const done = useSelector(getPasswordWasSaved);
  const { data, error, loading } = usePasswordRules(resetToken);
  const submitPassword = usePasswordReset(resetToken);
  const linkWasSent = useSelector(getResetLinkWasSent);

  if (done) {
    navigate(LOGIN);
    return <Content>Password Saved.</Content>;
  }

  if (loading) {
    return <Content>Loading... </Content>;
  }
  if (linkWasSent) {
    return <LinkWasSent />;
  }
  if (error) {
    return <RequestReset />;
  }

  const onSubmit = (password: string) => {
    submitPassword({ password });
  };

  return (
    <Content>
      <PasswordResetForm currentRuleSet={data} onSubmit={onSubmit} />
    </Content>
  );
};

export default withBranding(UNAUTHENTICATED)(PasswordReset);
